import type {
  ChatPlayerAuthoredMessage,
  ChatPlayerAuthoredPart,
  ChatPlayerMessage,
  ChatPlayerPartTiming,
  ChatPlayerScenarioSource,
} from "./chat-player-types";
import { normalizeChatPlayerCompiledChat } from "./chat-player-normalize";
import {
  clampMs,
  estimateSpeechDurationMs,
  makeStableId,
  normalizeTimedWordSpacing,
  synthesizeTimedWords,
} from "./chat-player-timing";

const MESSAGE_GAP_MS = 420;
const PART_GAP_MS = 150;
const TOOL_DEFAULT_DURATION_MS = 1200;
const CUSTOM_DEFAULT_DURATION_MS = 700;

type ScenarioPartTiming = {
  durationMs: number;
  immediate: boolean;
  startMs: number;
};

export function compileChatPlayerScenario(source: ChatPlayerScenarioSource) {
  const messages: ChatPlayerMessage[] = [];
  const partTimings: ChatPlayerPartTiming[] = [];
  let cursorMs = source.initialMessages?.length ? MESSAGE_GAP_MS : 0;

  source.initialMessages?.forEach((message, index) => {
    messages.push(
      compileScenarioMessage(message, {
        fallbackId: `initial-${index}`,
        immediate: true,
        partTimings,
        startMs: 0,
      }).message,
    );
  });

  source.messages.forEach((message, index) => {
    const compiled = compileScenarioMessage(message, {
      fallbackId: `message-${index}`,
      immediate: false,
      partTimings,
      startMs: cursorMs,
    });

    messages.push(compiled.message);
    cursorMs = Math.max(cursorMs, compiled.endMs + MESSAGE_GAP_MS);
  });

  return normalizeChatPlayerCompiledChat({
    id: source.id ?? "scenario-chat",
    messages,
    partTimings,
  });
}

function compileScenarioMessage(
  message: ChatPlayerAuthoredMessage,
  {
    fallbackId,
    immediate,
    partTimings,
    startMs,
  }: {
    fallbackId: string;
    immediate: boolean;
    partTimings: ChatPlayerPartTiming[];
    startMs: number;
  },
) {
  const messageId = message.id ?? fallbackId;
  const parts: ChatPlayerMessage["parts"] = [];
  let cursorMs = clampMs(startMs);

  message.parts.forEach((part, index) => {
    const partId = part.id ?? makeStableId([messageId, part.type, index]);
    const partStartMs = immediate ? 0 : cursorMs + (index === 0 ? 0 : PART_GAP_MS);
    const compiled = compileScenarioPart(part, {
      immediate,
      partId,
      startMs: partStartMs,
    });

    parts.push(compiled.part);
    partTimings.push({
      messageId,
      partId: compiled.part.id,
      startMs: compiled.startMs,
      endMs: compiled.endMs,
    });

    cursorMs = immediate ? 0 : compiled.endMs;
  });

  return {
    message: {
      id: messageId,
      role: message.role,
      parts,
    },
    endMs: cursorMs,
  };
}

function compileScenarioPart(
  part: ChatPlayerAuthoredPart,
  {
    immediate,
    partId,
    startMs,
  }: {
    immediate: boolean;
    partId: string;
    startMs: number;
  },
) {
  const normalizedStartMs = clampMs(startMs);

  switch (part.type) {
    case "speech": {
      const durationMs = part.durationMs ?? estimateSpeechDurationMs(part.content);
      const endMs = getScenarioPartEndMs({ durationMs, immediate, startMs: normalizedStartMs });
      const words = normalizeTimedWordSpacing(
        synthesizeTimedWords({
          text: part.content,
          idPrefix: partId,
          startMs: normalizedStartMs,
          endMs,
          immediate,
        }),
      );
      const compiledPart = { id: partId, type: "speech", words } satisfies ChatPlayerMessage["parts"][number];

      return {
        part: compiledPart,
        startMs: normalizedStartMs,
        endMs,
      };
    }

    case "text": {
      const durationMs = part.durationMs ?? 0;
      const compiledPart = {
        id: partId,
        type: "text",
        content: part.content,
      } satisfies ChatPlayerMessage["parts"][number];

      return {
        part: compiledPart,
        startMs: normalizedStartMs,
        endMs: getScenarioPartEndMs({ durationMs, immediate, startMs: normalizedStartMs }),
      };
    }

    case "tool": {
      const durationMs = part.durationMs ?? TOOL_DEFAULT_DURATION_MS;
      const compiledPart = {
        id: partId,
        type: "tool",
        name: part.name,
        data: part.data,
      } satisfies ChatPlayerMessage["parts"][number];

      return {
        part: compiledPart,
        startMs: normalizedStartMs,
        endMs: getScenarioPartEndMs({ durationMs, immediate, startMs: normalizedStartMs }),
      };
    }

    case "custom": {
      const durationMs = part.durationMs ?? CUSTOM_DEFAULT_DURATION_MS;
      const compiledPart = {
        id: partId,
        type: "custom",
        renderKey: part.renderKey,
        data: part.data,
      } satisfies ChatPlayerMessage["parts"][number];

      return {
        part: compiledPart,
        startMs: normalizedStartMs,
        endMs: getScenarioPartEndMs({ durationMs, immediate, startMs: normalizedStartMs }),
      };
    }
  }
}

function getScenarioPartEndMs({ durationMs, immediate, startMs }: ScenarioPartTiming) {
  return immediate ? startMs : startMs + durationMs;
}
